import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, ShieldCheck } from "lucide-react";
import { api, errMsg, roleLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MsLogo = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true"><rect x="1" y="1" width="9" height="9" fill="#f25022" /><rect x="11" y="1" width="9" height="9" fill="#7fba00" /><rect x="1" y="11" width="9" height="9" fill="#00a4ef" /><rect x="11" y="11" width="9" height="9" fill="#ffb900" /></svg>
);
const ERRORS = { unauthorized: "Accès non autorisé, contactez la comptabilité", state: "Session de connexion expirée, réessayez.", token: "Échec de l'authentification Microsoft.", disabled: "Compte désactivé, contactez la comptabilité.", mfa: "Le rôle super administrateur exige l'authentification multifacteur Microsoft. Votre connexion n'a pas utilisé la MFA : activez la règle d'accès conditionnel MFA pour le groupe APP-Orfacto-SuperAdmin dans Entra, puis reconnectez-vous." };

export default function Login() {
  const { user, loginWithToken } = useAuth();
  const [cfg, setCfg] = useState({ demo: false, demo_accounts: [], m365: true });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const nav = useNavigate();
  const loc = useLocation();
  useEffect(() => { api.get("/auth/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const hashToken = new URLSearchParams(loc.hash.slice(1)).get("token");
    if (hashToken) {
      window.history.replaceState(null, "", "/login");
      loginWithToken(hashToken).then(() => nav("/"));
    }
    if (params.get("error")) setError(ERRORS[params.get("error")] || "Erreur de connexion");
  }, [params, loc.hash]); // eslint-disable-line
  useEffect(() => { if (user) nav("/"); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/auth/login", { email, password });
      await loginWithToken(r.data.token);
      nav("/");
    } catch (err) {
      setError(errMsg(err));
    }
    setBusy(false);
  };
  const microsoft = async () => {
    if (!cfg.m365) return toast.info("Microsoft 365 n'est pas encore configuré : utilisez un compte de démonstration ci-dessous.");
    try {
      const r = await api.get("/auth/microsoft/login");
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="grain relative flex min-h-screen items-center justify-center overflow-hidden bg-[#16163F] p-4">
      <img src="/login-bg.jpg" alt="" aria-hidden="true" className="login-bg pointer-events-none absolute inset-0 h-full w-full object-cover object-[42%_center]" data-testid="login-background" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#16163F]/95 via-[#16163F]/75 to-[#16163F]/45" />
      <div className="pointer-events-none absolute inset-0 bg-[#A5894E]/10 mix-blend-multiply" />
      <div className="pointer-events-none absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full border border-[#A5894E]/25" />
      <div className="pointer-events-none absolute -right-24 -bottom-24 h-[360px] w-[360px] rounded-full border border-[#A5894E]/20" />
      <div className="rise relative flex w-full max-w-md flex-col items-center">
      <div className="mb-8 text-center" data-testid="login-brand">
        <h1 className="text-5xl font-extrabold tracking-tight text-white sm:text-6xl"><span className="text-[#A5894E]">OD</span>-Fact</h1>
        <div className="mx-auto mt-3 h-0.5 w-16 bg-[#A5894E]" />
        <p className="mt-4 text-base text-white">De la commande à la comptabilité</p>
        <p className="mt-1 text-xs font-semibold uppercase tracking-[0.3em] text-[#A5894E]">Groupe Omer-Decugis &amp; Cie</p>
      </div>
      <div className="relative w-full rounded-xl border border-[#E4E4EC] bg-white p-8 shadow-2xl sm:p-10">
        <div className="absolute inset-x-10 top-0 h-1 rounded-b bg-[#A5894E]" />
        <h2 className="text-xl font-extrabold tracking-tight">Connexion</h2>
        <p className="mt-1 text-sm text-[#6B6B80]">Achats, factures fournisseurs, équipements et immobilisations des sociétés du groupe.</p>
        {error && <div className="mt-5 rounded-md border border-[#B03A3A]/30 bg-[#B03A3A]/10 px-3 py-2 text-sm font-semibold text-[#B03A3A]" data-testid="login-error">{error}</div>}
        <button onClick={microsoft} className="mt-6 flex w-full items-center justify-center gap-3 rounded-md bg-[#16163F] px-4 py-3 text-sm font-bold text-white shadow-lg transition-colors hover:bg-[#2a2a5c]" data-testid="login-ms-button">
          <MsLogo /> Se connecter avec Microsoft
        </button>
        {!cfg.m365 && <p className="mt-2 text-center text-[11px] text-[#6B6B80]" data-testid="demo-mode-note">Mode démonstration : Microsoft 365 non configuré</p>}
        {cfg.demo && <>
        <div className="my-6 flex items-center gap-3 text-[11px] uppercase tracking-wider text-[#6B6B80]"><span className="h-px flex-1 bg-[#E4E4EC]" />Compte local<span className="h-px flex-1 bg-[#E4E4EC]" /></div>
        <form onSubmit={submit} className="space-y-3">
          <input className="inp" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
          <input className="inp" type="password" placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
          <button className="btn-secondary w-full justify-center" disabled={busy} data-testid="login-submit-button"><KeyRound size={16} /> Se connecter</button>
        </form>
        </>}
        {cfg.demo && cfg.demo_accounts.length > 0 && (
          <div className="mt-6 rounded-lg border border-dashed border-[#A5894E] p-4" data-testid="demo-accounts">
            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide"><ShieldCheck size={14} className="text-[#A5894E]" /> Comptes de démonstration</div>
            <div className="space-y-1">
              {cfg.demo_accounts.map((a) => (
                <button key={a.email} type="button" className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs hover:bg-[#A5894E]/10"
                  onClick={() => { setEmail(a.email); setPassword(a.password); }} data-testid={`demo-account-${a.email.split("@")[0].replace(".", "-")}`}>
                  <span className="font-semibold">{roleLabel(a)}{a.cost_centers?.length ? ` (${a.cost_centers.join(", ")})` : a.role === "comptable" ? ` (${a.companies?.length ? a.companies.join(", ") : "tout le groupe"})` : ""}</span><span className="mono text-[#6B6B80]">{a.email}</span>
                </button>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-[#6B6B80]">Mot de passe commun : <span className="mono font-semibold text-[#16163F]">{cfg.demo_accounts[0].password}</span></p>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
